import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollText, Filter, RefreshCw, User, Activity } from 'lucide-react';
import { auditTrailService } from '../services/api';
import { toast } from 'sonner';

const ACTION_COLORS = {
  CREATE: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  UPDATE: 'bg-sky-100 text-sky-800 border-sky-300',
  DELETE: 'bg-red-100 text-red-800 border-red-300',
  DUPLICATE: 'bg-violet-100 text-violet-800 border-violet-300',
  LOGIN: 'bg-slate-100 text-slate-800 border-slate-300',
  LOGOUT: 'bg-slate-100 text-slate-600 border-slate-300',
  LOGIN_FAILED: 'bg-red-100 text-red-700 border-red-300',
  EMAIL_SENT: 'bg-amber-100 text-amber-800 border-amber-300',
  NEW_VERSION: 'bg-violet-100 text-violet-800 border-violet-300',
};

const ENTITY_LABELS = {
  order: 'Ordine',
  order_line: 'Riga Ordine',
  production_lot: 'Lotto Produzione',
  lot_progress: 'Avanzamento Lotto',
  simplified_progress: 'Avanzamento Semplificato',
  delivery: 'Consegna',
  non_conformity: 'Non Conformita',
  control_plan: 'Piano Controllo',
  executed_control: 'Controllo Eseguito',
  work_cycle: 'Ciclo Lavoro',
  work_phase: 'Fase Lavoro',
  product: 'Prodotto',
  product_revision: 'Revisione Prodotto',
  client: 'Cliente',
  supplier: 'Fornitore',
  supplier_certification: 'Certificazione Fornitore',
  user: 'Utente',
  machine: 'Macchina',
  maintenance: 'Manutenzione',
  employee: 'Dipendente',
  shipment_calendar: 'Calendario Spedizioni',
  commercial_offer: 'Offerta Commerciale',
  auth: 'Autenticazione',
  email_report: 'Report Email',
};

const fmtTs = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
};

export default function AuditTrailPage() {
  const [logs, setLogs] = useState([]);
  const [entities, setEntities] = useState({ entity_types: [], actions: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    user_email: '',
    date_from: '',
    date_to: '',
    limit: 200,
  });

  useEffect(() => {
    loadEntities();
    loadStats();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadEntities = async () => {
    try {
      const r = await auditTrailService.entities();
      setEntities(r.data || { entity_types: [], actions: [] });
    } catch { /* ignore */ }
  };

  const loadStats = async () => {
    try {
      const r = await auditTrailService.stats();
      setStats(r.data);
    } catch { /* ignore */ }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.keys(filters).forEach(k => { if (filters[k] !== '' && filters[k] != null) params[k] = filters[k]; });
      const r = await auditTrailService.list(params);
      setLogs(r.data || []);
    } catch {
      toast.error('Errore caricamento audit log');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setFilters({ entity_type: '', action: '', user_email: '', date_from: '', date_to: '', limit: 200 });

  const userCounts = useMemo(() => {
    const m = {};
    logs.forEach(l => { if (l.user_email) m[l.user_email] = (m[l.user_email] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [logs]);

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-slate-700" />
            Audit Trail
          </h1>
          <p className="text-sm text-slate-500 mt-1">Tracciamento azioni utenti sul sistema (compliance IATF / AS9100)</p>
        </div>
        <Button onClick={loadLogs} variant="outline" className="h-10" data-testid="refresh-audit-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-5">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Azioni (ultimi 7gg)</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_last_7d}</p>
            </CardContent>
          </Card>
          {(stats.by_action || []).slice(0, 3).map((a) => (
            <Card key={a.action} className="border-l-4 border-l-slate-400">
              <CardContent className="p-5">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">{a.action}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{a.count}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Top users */}
      {userCounts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4 text-slate-700" />
              Utenti piu attivi (record correnti)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {userCounts.map(([email, count]) => (
                <Badge key={email} className="bg-slate-100 text-slate-700 border border-slate-300 px-3 py-1">
                  {email} <span className="ml-2 font-bold text-slate-900">{count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Entita</Label>
              <Select value={filters.entity_type || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, entity_type: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-entity"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutte</SelectItem>
                  {(entities.entity_types || []).map(t => <SelectItem key={t} value={t}>{ENTITY_LABELS[t] || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Azione</Label>
              <Select value={filters.action || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, action: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-action"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {(entities.actions || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Utente (email)</Label>
              <Input value={filters.user_email} onChange={(e) => setFilters(f => ({ ...f, user_email: e.target.value }))} placeholder="es. admin@evotech.it" data-testid="filter-user" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Dal</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Al</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Limite</Label>
              <Select value={String(filters.limit)} onValueChange={(v) => setFilters(f => ({ ...f, limit: Number(v) }))}>
                <SelectTrigger data-testid="filter-limit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="outline" size="sm" onClick={reset} data-testid="reset-audit-filters">Reset filtri</Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p>Nessuna azione registrata per i filtri selezionati</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Timestamp</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Azione</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Entita</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">ID</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Utente</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Endpoint</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">HTTP</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                      <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{fmtTs(l.timestamp)}</td>
                      <td className="px-3 py-2">
                        <Badge className={`${ACTION_COLORS[l.action] || 'bg-slate-100 text-slate-700 border-slate-300'} border`}>{l.action}</Badge>
                      </td>
                      <td className="px-3 py-2 font-medium">{ENTITY_LABELS[l.entity_type] || l.entity_type}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500 max-w-[180px] truncate" title={l.entity_id}>{l.entity_id || '-'}</td>
                      <td className="px-3 py-2 text-slate-700">{l.user_email || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500 max-w-[260px] truncate" title={l.path}>
                        <span className="text-slate-700 font-semibold mr-1">{l.method}</span>
                        {l.path}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-mono ${l.status_code >= 400 ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{l.status_code || ''}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{l.ip_address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
